import { usePermission } from '@/lib/hooks/use-permission'
import { resolveRouteRequirement } from '@/lib/utils/route-access'
import { ReactNode } from 'react'
import { MdLock } from 'react-icons/md'
import { useLocation, useNavigate } from 'react-router-dom'

/**
 * Guard de rota do painel admin: bloqueia o acesso direto via URL a
 * modulos/recursos que o usuario nao possui (o sidebar ja esconde, mas a rota
 * era acessivel digitando a URL). Defesa em profundidade — o backend continua
 * sendo a autoridade sobre os dados.
 *
 * Rotas nao mapeadas (welcome, /account, /core/*) e usuarios com bypass
 * (MASTER/COMPANY, tratados em usePermission) passam livremente.
 */
export function RouteAccessGuard({ children }: { children: ReactNode }) {
    const location = useLocation()
    const { canRead, canAccessModule } = usePermission()

    const requirement = resolveRouteRequirement(location.pathname)

    let allowed = true
    if (requirement?.kind === 'resource') {
        allowed = requirement.options.some(o => canRead(o.module, o.resource))
    } else if (requirement?.kind === 'module') {
        allowed = canAccessModule(requirement.module)
    }

    if (!allowed) {
        return <AccessDenied />
    }

    return <>{children}</>
}

function AccessDenied() {
    const navigate = useNavigate()
    return (
        <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 text-center px-6">
            <div className="w-16 h-16 rounded-2xl bg-error/10 flex items-center justify-center">
                <MdLock className="text-3xl text-error" />
            </div>
            <div>
                <h1 className="text-xl font-bold text-base-content">
                    Acesso restrito
                </h1>
                <p className="text-base-content/60 text-sm mt-1 max-w-md">
                    Você não tem permissão para acessar esta área. Fale com o
                    administrador da sua organização se precisar de acesso.
                </p>
            </div>
            <button
                className="btn btn-secondary btn-sm"
                onClick={() => navigate('/')}
            >
                Voltar ao início
            </button>
        </div>
    )
}
